import {
  clone_deep,
  TABS,
  NEW_MEMBER_TEMPLATE,
  TEAM_MEMBERS,
  ACCESS_AREAS,
  ROLE_DEFAULTS,
  MEMBER_ACCESS_DEFAULTS,
  ROLES,
  NEW_ROLE_TEMPLATE,
  AVAILABLE_PERMISSIONS,
  ROLE_COLORS
} from './settings_constants.js';
import { get_token, get_user_profile } from "@/router";

function getGlobalDataTableHelpers() {
  if (typeof window === 'undefined' || !window.global_data_table || typeof window.global_data_table.create_component_helpers !== 'function') {
    return {};
  }

  return window.global_data_table.create_component_helpers({
    selector: '.global_data_table[data-search-placeholder="Search team members"]',
    data_key_to_watch: 'team_members'
  });
}

const globalDataTableHelpers = getGlobalDataTableHelpers();

const users_url = process.env.VUE_APP_VIEW_USER_URL || "";
const deactivate_user_url = process.env.VUE_APP_DEACTIVATE_USER_URL || process.env.VUE_APP_VIEW_USER_URL || "";
const users_list_url = process.env.VUE_APP_USER_LIST_URL || "";
const create_user_url = process.env.VUE_APP_CREATE_USER_URL || "";
const roles_url = process.env.VUE_APP_ROLES_URL || "";

export default {
  name: "settings_section",

  data() {
    return ({
      active_tab: "team",
      tabs: TABS,
      show_invite_modal: false,
      show_change_role_modal: false,
      show_delete_modal: false,
      selected_member: null,
      new_member: clone_deep(NEW_MEMBER_TEMPLATE),
      team_members: clone_deep(TEAM_MEMBERS),
      open_dropdown_id: null,
      access_areas: ACCESS_AREAS,
      role_defaults: ROLE_DEFAULTS,
      member_access: clone_deep(MEMBER_ACCESS_DEFAULTS),
      roles: clone_deep(ROLES),
      new_role: clone_deep(NEW_ROLE_TEMPLATE),
      show_role_modal: false,
      is_editing_role: false,
      editing_role_id: null,
      available_permissions: AVAILABLE_PERMISSIONS,
      role_colors: ROLE_COLORS,
      is_loading_users: false,
      is_loading_roles: false,
      is_saving_user: false,
      users_error: "",
      users_success: ""
    });
  },

  mounted() {
    document.addEventListener('click', this.close_dropdown);
    this.initialize_member_access();
    if (globalDataTableHelpers.mounted) {
      globalDataTableHelpers.mounted.call(this);
    }
    this.load_team_member_data();
  },

  beforeUnmount() {
    document.removeEventListener('click', this.close_dropdown);
    if (globalDataTableHelpers.beforeUnmount) {
      globalDataTableHelpers.beforeUnmount.call(this);
    }
  },

  computed: {
    active_members_count() {
      return this.team_members.filter(member => member.status === "active").length;
    },
    
    invited_members_count() {
      return this.team_members.filter(member => member.status === "invited").length;
    },
    
    full_name() {
      return `${this.new_member.first_name} ${this.new_member.last_name}`.trim();
    },
    
    // NEW: Get active members for access control
    active_members() {
      return this.team_members.filter(member => member.status === "active");
    },
    
    // NEW: Get total members by role
    members_by_role() {
      const result = {};
      this.roles.forEach(role => {
        result[role.name] = this.team_members.filter(member => member.role === role.name).length;
      });
      return result;
    }
  },

  watch: {
    ...globalDataTableHelpers.watch
  },

  methods: {
    get_auth_headers(include_json_content_type = true) {
      const auth_token = get_token();
      const headers = {
        Accept: "application/json"
      };

      if (include_json_content_type) {
        headers["Content-Type"] = "application/json";
      }

      if (auth_token) {
        headers.Authorization = `Token ${auth_token}`;
      }

      return headers;
    },

    async get_response_data(response, fallback_message) {
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(this.get_error_message(data, fallback_message));
      }

      return data;
    },

    get_error_message(data, fallback_message = "Unable to complete user request.") {
      const error_data = data?.errors || data?.detail || data?.error || data?.message || data;

      if (!error_data) {
        return fallback_message;
      }

      if (typeof error_data === "string") {
        return error_data;
      }

      if (Array.isArray(error_data)) {
        return error_data.join(", ");
      }

      if (typeof error_data === "object") {
        return Object.entries(error_data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
          .join(" | ");
      }

      return fallback_message;
    },

    extract_collection(response_data, collection_keys = ["data", "results", "users"]) {
      if (Array.isArray(response_data)) {
        return response_data;
      }

      if (!response_data || typeof response_data !== "object") {
        return [];
      }

      for (const key of collection_keys) {
        const value = response_data[String(key)];

        if (Array.isArray(value)) {
          return value;
        }

        if (value && typeof value === "object") {
          const nested_collection = this.extract_collection(value, collection_keys);

          if (nested_collection.length) {
            return nested_collection;
          }
        }
      }

      return [];
    },

    async load_team_member_data() {
      await this.load_roles();
      await this.load_users();
    },

    async load_roles() {
      this.is_loading_roles = true;

      try {
        if (!roles_url || !get_token()) {
          return;
        }

        const response = await fetch(roles_url, {
          method: "GET",
          headers: this.get_auth_headers(false)
        });
        const roles_data = await this.get_response_data(response, "Unable to load roles.");
        const next_roles = this.extract_collection(roles_data, ["data", "results", "roles"]);

        if (next_roles.length) {
          this.roles = next_roles.map((role, index) => this.get_role_card(role, index));
        }
      } catch (error) {
        this.users_error = error.message || "Unable to load roles.";
      } finally {
        this.is_loading_roles = false;
      }
    },

    get_role_card(role, index) {
      const fallback_role = ROLES[parseInt(index, 10)] || {};
      const role_name = role?.name || fallback_role.name || "Role";

      return ({
        id: role?.id ?? fallback_role.id ?? role_name,
        name: role_name,
        short_name: role?.short_name || role_name.toLowerCase().replace(/\s+/g, "_"),
        description: role?.description || fallback_role.description || `${role_name} access`,
        permissions: role?.permissions || fallback_role.permissions || [],
        member_count: this.team_members.filter((member) => member.role === role_name).length,
        color: fallback_role.color || this.role_colors[index % this.role_colors.length]?.value || "secondary",
        raw: role
      });
    },

    async load_users() {
      this.is_loading_users = true;
      this.users_error = "";

      try {
        if (!users_list_url || !get_token()) {
          this.users_error = !users_list_url
            ? "VUE_APP_USER_LIST_URL is not configured."
            : "No auth token found.";
          return;
        }

        const response = await fetch(users_list_url, {
          method: "GET",
          headers: this.get_auth_headers(false)
        });
        const users_data = await this.get_response_data(response, "Unable to load team members.");
        const users = this.extract_collection(users_data, ["data", "results", "users"]);

        this.team_members = users.map((user) => this.get_member_row(user));
        this.refresh_role_member_counts();
        this.initialize_member_access();
      } catch (error) {
        this.users_error = error.message || "Unable to load team members.";
      } finally {
        this.is_loading_users = false;
      }
    },

    get_member_row(user) {
      const role = user?.user_role?.role || {};
      const profile = user?.profile || user?.user_profile || {};
      const first_name = user?.first_name || "";
      const last_name = user?.last_name || "";
      const email = user?.email || "";
      const name = `${first_name} ${last_name}`.trim() || email || user?.username || "User";

      return ({
        id: user?.id,
        name,
        first_name,
        last_name,
        username: user?.username || email,
        email,
        phone_number: profile?.phone_number || user?.phone_number || "",
        role: role?.name || "",
        role_id: role?.id ?? null,
        status: user?.is_active === false ? "inactive" : "active",
        last_active: user?.last_login ? new Date(user.last_login).toLocaleDateString() : "Never",
        raw: user
      });
    },

    get_role_id_from_name(role_name) {
      const role = this.roles.find((item) => item.name === role_name);
      return role?.id ?? null;
    },

    get_user_payload(member) {
      const role_id = this.get_role_id_from_name(member.role);
      const payload = {
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        username: member.email,
        phone_number: member.phone_number || "",
        client_app: 'npi'
      };

      if (role_id) {
        payload.role_id = role_id;
      }

      return payload;
    },

    refresh_role_member_counts() {
      this.roles = this.roles.map((role) => ({
        ...role,
        member_count: this.team_members.filter((member) => member.role === role.name).length
      }));
    },

    // NEW: Get access status for a member and area
    get_member_access(member_id, area_id) {
      // If member has specific access setting, use that
      const member_key = String(member_id);
      const area_key = String(area_id);

      if (this.member_access[String(member_key)] && this.member_access[String(member_key)][String(area_key)] !== undefined) {
        return this.member_access[String(member_key)][String(area_key)];
      }
      
      // Otherwise use role default
      const member = this.team_members.find(m => m.id === member_id);
      if (member && this.role_defaults[String(member.role)]) {
        return this.role_defaults[String(member.role)][String(area_key)] || false;
      }
      
      return false;
    },
    
    // NEW: Handle access change - simplified for v-model
    on_access_change(member_id, area_id) {
      // Show feedback
      const member = this.team_members.find(m => m.id === member_id);
      const area = this.access_areas.find(a => a.id === area_id);
      if (member && area) {
        const status = this.member_access[String(member_id)][String(area_id)] ? "granted" : "revoked";
        console.log(`${area.name} access ${status} for ${member.name}`);
      }
    },
    
    // NEW: Reset to role defaults - FIXED for Vue 3
    reset_to_role_defaults(member_id) {
      const member = this.team_members.find(m => m.id === member_id);
      if (member && this.role_defaults[String(member.role)]) {
        // Reset each area individually to maintain reactivity properly
        this.access_areas.forEach(area => {
          const role_default = this.role_defaults[String(member.role)];
          this.member_access[String(member_id)][String(area.id)] = role_default ? role_default[String(area.id)] || false : false;
        });
        alert(`Access reset to ${member.role} defaults for ${member.name}`);
      }
    },
    
    // NEW: Save all access changes
    save_access_changes() {
      // In a real app, you would save to API
      alert("Access control changes saved successfully!");
    },
    
    toggle_dropdown(member_id, event) {
      event.preventDefault();
      event.stopPropagation();
      
      if (this.open_dropdown_id === member_id) {
        this.open_dropdown_id = null;
      } else {
        this.open_dropdown_id = member_id;
      }
    },
    
    close_dropdown(event) {
      if (!event.target.closest('.dropdown')) {
        this.open_dropdown_id = null;
      }
    },
    
    // Initialize member access for all members and areas
    initialize_member_access() {
      this.team_members.forEach(member => {
        const member_key = String(member.id);

        if (!this.member_access[String(member_key)]) {
          this.member_access[String(member_key)] = {};
        }
        
        // Initialize each area for this member
        this.access_areas.forEach(area => {
          const area_key = String(area.id);

          if (this.member_access[String(member_key)][String(area_key)] === undefined) {
            // Administrators always have full access
            if (member.role === 'Administrator') {
              this.member_access[String(member_key)][String(area_key)] = true;
            } else {
              // Use role defaults if available
              const role_default = this.role_defaults[String(member.role)];
              this.member_access[String(member_key)][String(area_key)] = role_default ? role_default[String(area_key)] || false : false;
            }
          }
        });
      });
    },

    invite_member() {
      this.users_error = "";
      this.users_success = "";
      this.show_invite_modal = true;
      this.open_dropdown_id = null;
    },
    
    async submit_invite() {
      if (!this.new_member.first_name.trim()) {
        alert("Please enter first name");
        return;
      }
      
      if (!this.new_member.last_name.trim()) {
        alert("Please enter last name");
        return;
      }
      
      if (!this.new_member.email) {
        alert("Please enter an email address");
        return;
      }
      
      const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email_regex.test(this.new_member.email)) {
        alert("Please enter a valid email address");
        return;
      }
      
      const existing_member = this.team_members.find(
        member => member.email.toLowerCase() === this.new_member.email.toLowerCase()
      );
      
      if (existing_member) {
        alert("This email is already in the team");
        return;
      }

      this.is_saving_user = true;
      this.users_error = "";

      try {
        const payload = this.get_user_payload(this.new_member);

        if (create_user_url) {
          const response = await fetch(create_user_url, {
            method: "POST",
            headers: this.get_auth_headers(true),
            body: JSON.stringify(payload)
          });
          await this.get_response_data(response, "Unable to invite team member.");
          await this.load_users();
        }

        this.users_success = "Team member invited successfully.";
        this.reset_form();
        this.show_invite_modal = false;
      } catch (error) {
        this.users_error = error.message || "Unable to invite team member.";
      } finally {
        this.is_saving_user = false;
      }
    },
    
    open_change_role_modal(member) {
      const name_parts = (member.name || "").trim().split(" ");
      const first_name = member.first_name || name_parts[0] || "";
      const last_name = member.last_name || name_parts.slice(1).join(" ") || "";

      this.selected_member = {
        ...member,
        first_name,
        last_name
      };
      this.show_change_role_modal = true;
      this.open_dropdown_id = null;
    },
    
    async submit_change_role() {
      if (!this.selected_member) return;

      this.is_saving_user = true;
      this.users_error = "";

      try {
        const base_url = users_url.endsWith("/") ? users_url : `${users_url}/`;
        const response = await fetch(`${base_url}${this.selected_member.id}/update/`, {
          method: "POST",
          headers: this.get_auth_headers(true),
          body: JSON.stringify(this.get_user_payload(this.selected_member))
        });
        await this.get_response_data(response, "Unable to update team member.");
        this.users_success = "Team member updated successfully.";
        this.close_change_role_modal();
        await this.load_users();
      } catch (error) {
        this.users_error = error.message || "Unable to update team member.";
      } finally {
        this.is_saving_user = false;
      }
    },
    
    close_change_role_modal() {
      this.show_change_role_modal = false;
      this.selected_member = null;
    },
    
    open_delete_modal(member) {
      this.selected_member = { ...member };
      this.show_delete_modal = true;
      this.open_dropdown_id = null;
    },
    
    async submit_delete() {
      if (!this.selected_member) return;

      const current_user = get_user_profile();
      if (String(current_user?.id || "") === String(this.selected_member.id || "")) {
        this.users_error = "You cannot deactivate your own account.";
        this.close_delete_modal();
        return;
      }

      this.is_saving_user = true;
      this.users_error = "";

      try {
        const base_url = deactivate_user_url.endsWith("/") ? deactivate_user_url : `${deactivate_user_url}/`;
        const response = await fetch(`${base_url}${this.selected_member.id}/deactivate/`, {
          method: "POST",
          headers: this.get_auth_headers(true)
        });
        await this.get_response_data(response, "Unable to deactivate team member.");
        this.users_success = "Team member deactivated successfully.";
        this.close_delete_modal();
        await this.load_users();
      } catch (error) {
        this.users_error = error.message || "Unable to deactivate team member.";
      } finally {
        this.is_saving_user = false;
      }
    },
    
    close_delete_modal() {
      this.show_delete_modal = false;
      this.selected_member = null;
    },
    
    accept_invitation(member) {
      if (confirm(`Accept invitation for ${member.name}?`)) {
        const member_index = this.team_members.findIndex(m => m.id === member.id);
        if (member_index !== -1) {
          this.team_members[parseInt(member_index, 10)].status = "active";
          this.team_members[parseInt(member_index, 10)].last_active = "Just now";
          alert(`${member.name} is now an active team member`);
        }
      }
      this.open_dropdown_id = null;
    },
    
    resend_invitation(member) {
      alert(`Invitation email resent to ${member.email}`);
      this.open_dropdown_id = null;
    },
    
    reset_form() {
      this.new_member = {
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        role: this.roles[0]?.name || "",
        message: ""
      };
    },
    
    close_invite_modal() {
      this.show_invite_modal = false;
      this.reset_form();
    },
    
    get_initials(name) {
      return name.split(' ').map(word => word.charAt(0)).join('');
    },
    
    // NEW: Role Management Methods
    
    open_create_role_modal() {
      this.new_role = {
        name: "",
        short_name: "",
        description: "",
        permissions: [],
        color: "light"
      };
      this.is_editing_role = false;
      this.editing_role_id = null;
      this.show_role_modal = true;
    },
    
    open_edit_role_modal(role) {
      this.new_role = {
        name: role.name,
        short_name: role.short_name,
        description: role.description,
        permissions: [...role.permissions],
        color: role.color
      };
      this.is_editing_role = true;
      this.editing_role_id = role.id;
      this.show_role_modal = true;
    },
    
    submit_role() {
      if (!this.new_role.name.trim()) {
        alert("Please enter a role name");
        return;
      }
      
      if (!this.new_role.short_name.trim()) {
        alert("Please enter a short name");
        return;
      }
      
      if (!this.new_role.description.trim()) {
        alert("Please enter a description");
        return;
      }
      
      if (this.is_editing_role && this.editing_role_id) {
        // Update existing role
        const role_index = this.roles.findIndex(r => r.id === this.editing_role_id);
        if (role_index !== -1) {
          // Update member count based on actual team members
          const member_count = this.team_members.filter(m => m.role === this.new_role.name).length;
          
          this.roles[parseInt(role_index, 10)] = {
            ...this.roles[parseInt(role_index, 10)],
            name: this.new_role.name,
            short_name: this.new_role.short_name,
            description: this.new_role.description,
            permissions: this.new_role.permissions,
            color: this.new_role.color,
            member_count: member_count
          };
          // Force reactivity for Vue 3
          this.roles = [...this.roles];
          alert(`Role "${this.new_role.name}" updated successfully`);
        }
      } else {
        // Create new role
        const new_id = this.roles.length > 0 
          ? Math.max(...this.roles.map(r => r.id)) + 1 
          : 1;
        
        this.roles.push({
          id: new_id,
          name: this.new_role.name,
          short_name: this.new_role.short_name,
          description: this.new_role.description,
          permissions: this.new_role.permissions,
          member_count: 0,
          color: this.new_role.color
        });
        // Force reactivity for Vue 3
        this.roles = [...this.roles];
        alert(`Role "${this.new_role.name}" created successfully`);
      }
      
      this.close_role_modal();
    },
    
    close_role_modal() {
      this.show_role_modal = false;
      this.new_role = {
        name: "",
        short_name: "",
        description: "",
        permissions: [],
        color: "light"
      };
      this.is_editing_role = false;
      this.editing_role_id = null;
    },
    
    delete_role(role) {
      if (confirm(`Are you sure you want to delete the "${role.name}" role? This action cannot be undone.`)) {
        // Check if any members have this role
        const members_with_role = this.team_members.filter(m => m.role === role.name);
        if (members_with_role.length > 0) {
          alert(`Cannot delete role. ${members_with_role.length} team member(s) have this role. Please reassign them first.`);
          return;
        }
        
        this.roles = this.roles.filter(r => r.id !== role.id);
        // Force reactivity for Vue 3
        this.roles = [...this.roles];
        alert(`Role "${role.name}" deleted successfully`);
      }
    },
    
    toggle_permission(permission) {
      const index = this.new_role.permissions.indexOf(permission);
      if (index === -1) {
        this.new_role.permissions.push(permission);
      } else {
        this.new_role.permissions.splice(index, 1);
      }
      // Force reactivity for Vue 3
      this.new_role.permissions = [...this.new_role.permissions];
    },
    
    is_permission_selected(permission) {
      return this.new_role.permissions.includes(permission);
    },
    get_badge_class(role) {
      return `bg-${role.color} ${role.color === 'light' ? 'text-dark' : 'text-white'}`;
    }
  }
};
